import React, { useCallback, useEffect, useState } from 'react'
import { BackHandler, Platform, TouchableOpacity, useWindowDimensions } from 'react-native'
import Animated, { cancelAnimation, useAnimatedStyle, useReducedMotion, useSharedValue, withTiming } from 'react-native-reanimated'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'

export type ScreenDrawerDismiss = (
  afterDismiss: () => void,
  options?: { reuseHistoryEntry?: boolean; parkImmediately?: boolean },
) => void

interface ScreenDrawerProps {
  visible: boolean
  onClose: () => void
  top: number
  left?: number
  slideFrom?: 'right' | 'bottom'
  animateExitOnWeb?: boolean
  accessibilityLabel: string
  backdropLabel: string
  backdropTestID?: string
  testID?: string
  children: (dismissThenNavigate: ScreenDrawerDismiss) => React.ReactNode
}

/**
 * Shared screen-level overlay chrome for game drawers. The surface stays
 * mounted for its entrance/exit animation; while closed it is off-screen and
 * pointer-transparent. `dismissThenNavigate` lets drawer actions hand the web
 * back-stack entry to a route or sibling modal without exposing animation state.
 */
export function ScreenDrawer({
  visible,
  onClose,
  top,
  left = 0,
  slideFrom = 'right',
  animateExitOnWeb = false,
  accessibilityLabel,
  backdropLabel,
  backdropTestID,
  testID,
  children,
}: ScreenDrawerProps) {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const reducedMotion = useReducedMotion()
  const [visualFrame, setVisualFrame] = useState<{ top: number; bottom: number; left: number; right: number } | null>(null)
  const offset = useSharedValue(slideFrom === 'right' ? screenWidth : screenHeight)
  const panelStyle = useAnimatedStyle(() => ({
    transform: slideFrom === 'right' ? [{ translateX: offset.value }] : [{ translateY: offset.value }],
  }))
  // The backdrop shade (over the top bar too) fades with the slide: 0 when
  // parked off-screen, full when the drawer is in place.
  const closedOffsetForShade = slideFrom === 'right' ? screenWidth : screenHeight
  const backdropStyle = useAnimatedStyle(() => ({
    opacity: closedOffsetForShade > 0 ? Math.min(1, Math.max(0, 1 - offset.value / closedOffsetForShade)) : 1,
  }))

  useEffect(() => {
    const closedOffset = slideFrom === 'right' ? screenWidth : screenHeight
    cancelAnimation(offset)
    if (reducedMotion) {
      offset.value = visible ? 0 : closedOffset
      return
    }
    offset.value = withTiming(visible ? 0 : closedOffset, { duration: 240 })
  }, [visible, slideFrom, screenWidth, screenHeight, reducedMotion, offset])

  const dismissLayer = useWebModalHistory({ visible, canDismiss: true, onDismiss: onClose })
  const dismissThenNavigate = useCallback<ScreenDrawerDismiss>((afterDismiss, options) => {
    if (options?.parkImmediately) {
      cancelAnimation(offset)
      offset.value = slideFrom === 'right' ? screenWidth : screenHeight
    }
    dismissLayer(afterDismiss, options)
  }, [dismissLayer, offset, screenHeight, screenWidth, slideFrom])

  useEffect(() => {
    if (Platform.OS !== 'android' || !visible) return
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      onClose()
      return true
    })
    return () => subscription.remove()
  }, [visible, onClose])

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible || typeof window === 'undefined') {
      setVisualFrame(null)
      return
    }
    const update = () => {
      const viewport = window.visualViewport
      if (!viewport) {
        setVisualFrame(null)
        return
      }
      const viewportBottom = viewport.offsetTop + viewport.height
      setVisualFrame({
        top: Math.max(top, viewport.offsetTop),
        bottom: Math.max(0, window.innerHeight - viewportBottom),
        left: viewport.offsetLeft + left,
        right: Math.max(0, window.innerWidth - (viewport.offsetLeft + viewport.width)),
      })
    }
    update()
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', update)
    viewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      viewport?.removeEventListener('resize', update)
      viewport?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [visible, top, left])

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, onClose])

  const frameStyle = visualFrame
    ? { top: visualFrame.top, bottom: visualFrame.bottom, left: visualFrame.left, right: visualFrame.right }
    : { top, bottom: 0, left, right: 0 }

  return (
    <>
      <Animated.View
        pointerEvents={visible ? 'auto' : 'none'}
        style={[
          { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20, backgroundColor: 'rgba(0,0,0,0.35)' },
          // Web parks a closing drawer instantly unless asked to animate the
          // exit; its shade goes with it.
          Platform.OS === 'web' && !visible && !animateExitOnWeb ? { opacity: 0 } : backdropStyle,
        ]}
      >
        {visible && (
          <TouchableOpacity
            accessibilityLabel={backdropLabel}
            accessibilityRole="button"
            testID={backdropTestID}
            activeOpacity={1}
            onPress={onClose}
            style={{ flex: 1 }}
          />
        )}
      </Animated.View>
      <Animated.View
        pointerEvents={visible ? 'auto' : 'none'}
        accessibilityLabel={accessibilityLabel}
        accessibilityViewIsModal={visible}
        testID={testID}
        style={[
          { position: 'absolute', ...frameStyle, zIndex: 21 },
          Platform.OS === 'web' && !visible && !animateExitOnWeb
            ? slideFrom === 'right'
              ? { transform: [{ translateX: screenWidth }] }
              : { transform: [{ translateY: screenHeight }] }
            : panelStyle,
        ]}
      >
        {children(dismissThenNavigate)}
      </Animated.View>
    </>
  )
}
